import Grid from "@mui/material/Grid";

import { Coverage, CoverageEvent, PolicyType } from "@graphql/api";
import { useTheme } from "@mui/material";

import { DisplayPropertiesTable } from "@covertree/ui/content";
import { formatAwsDateString, formatCurrency } from "@covertree/utils";

import { mapCoverageStatus, mapPolicyTypeToPOCType } from "@utils";

import CoverageHomeDetails from "../CoverageHomeDetails/CoverageHomeDetails";

interface CoverageDetailsProps extends React.PropsWithChildren {
  coverage:
    | Coverage
    | (CoverageEvent["eventData"] & { id: string; policyType: PolicyType });
}

export function CoverageDetails({ children, coverage }: CoverageDetailsProps) {
  const theme = useTheme();

  return (
    <Grid
      container
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      justifyItems="flex-start"
      data-testid="coverage-details"
      gap={3}>
      {children}
      <DisplayPropertiesTable
        containerStyles={{
          padding: theme.spacing(3),
          width: "100%",
        }}
        properties={[
          {
            label: "Policy Type",
            value: mapPolicyTypeToPOCType(coverage.policyType),
          },
          {
            label: "Insurance Carrier",
            value: coverage.insuranceCarrierName,
          },
          {
            label: "Policy Status",
            value: mapCoverageStatus(coverage.isActive),
          },
          {
            label: "Description",
            value: coverage.policyDescription || "None",
          },
        ]}
      />
      <DisplayPropertiesTable
        containerStyles={{
          padding: theme.spacing(3),
          width: "100%",
        }}
        properties={[
          {
            label: "Policy Term",
            value: `${formatAwsDateString("policyEffectiveDate" in coverage ? coverage.policyEffectiveDate : coverage.termEffectiveDate)} -
              ${formatAwsDateString("policyEffectiveDate" in coverage ? coverage.policyEndDate : coverage.termEndDate)}
            `,
          },
          {
            label: "Policy ID",
            value: coverage.policyId,
          },
          {
            label: "Annual Cost",
            value: formatCurrency(coverage.annualCost),
          },
          {
            label: "Agency Name",
            value: coverage.agencyName || "N/A",
          },
          {
            label: "Agency Address",
            value: coverage.agencyAddress || "N/A",
          },
          {
            label: "Agency Phone Number",
            value: coverage.agencyPhoneNumber || "N/A",
          },
          {
            label: "Agency Email",
            value: coverage.agencyEmail || "N/A",
          },
          ...(coverage.policyType === PolicyType.External
            ? [
                {
                  label: "Covertree internal sales agent issued this policy",
                  value: coverage.isInternalSale ? "Yes" : "No",
                },
              ]
            : []),
        ]}
      />
      <CoverageHomeDetails coverage={coverage} expanded width="100%" />
    </Grid>
  );
}
